//-- Copyright (c) 2021 TMT Insights Group, LLC
import React, { memo, useEffect, useMemo, useRef, useState } from 'react';
import { isEqual } from 'lodash';
import styled from '@emotion/styled';
import { RJSFSchema } from '@rjsf/utils';
import type { ChipOwnProps } from '@mui/material/Chip';
import { Box, Button, Chip, Grid, Typography } from '@mui/material';
import AssetIcon from '../../Job/JobTabs/JobTabAssetStatus/AssetIcon';
import type { EditModalProfileData } from './OPProfileDetailsOutputEditModal';
import OPProfileDetailsOutputEditModal from './OPProfileDetailsOutputEditModal';
import OPOrderSpecSchemaFormReadOnly from './OPOrderSpecSchemaFormReadOnly';
import PolarisIcon from '@/components/_common/PolarisIcon';
import { selectAudioLanguages, selectSubtitlesLanguages } from '@/redux/selectors/constantsSelectors';
import ResetButton from '@/components/_common/IconButtons/ResetButton';
import SimpleLabelValue from '@/components/_common/SimpleLabelValue';
import { useAppSelector } from '@/redux/hooks';
import { selectProfileById } from '@/redux/selectors/profileSelectors';
import { useToast } from '@/components/_common/Toast';
import { ThemeColors } from '@/__configs/config_types';

export interface ProfileDetailsOutputProfile {
  id: number;
  name: string;
  data: EditModalProfileData;
}

interface Props {
  profile: ProfileDetailsOutputProfile;
  orderSpecSchema?: RJSFSchema;
  orderSpecInputs?: Record<string, any>;
  orderSpecSchemaFormBeforeMessage?: React.ReactNode;
  onSave(updatedProfile: ProfileDetailsOutputProfile['data']): void;
  updateOrderSpecInputs: (inputs: Record<string, any> | undefined) => void;
  outputRevisedLabelColor?: ThemeColors;
}

const pickFields = (profileData: EditModalProfileData) => {
  return {
    media_resolution: profileData.media_resolution,
    audio_tracks: profileData.audio_tracks,
    subtitle_tracks: profileData.subtitle_tracks
  };
};

const OPProfileDetailsOutput = ({
  profile,
  orderSpecSchema,
  orderSpecInputs,
  orderSpecSchemaFormBeforeMessage,
  updateOrderSpecInputs,
  onSave,
  outputRevisedLabelColor
}: Props) => {
  const AudioLanguages = useAppSelector(selectAudioLanguages);
  const SubtitleLanguages = useAppSelector(selectSubtitlesLanguages);
  const originalProfile = useAppSelector((state) => selectProfileById(state, profile.id));
  const [open, setOpen] = useState(false);
  const [orderSpecFormInitialInputs, setOrderSpecFormInitialInputs] = useState<Record<string, any> | undefined>(undefined);
  const [wasEdited, setWasEdited] = useState(false);
  const editModalWasOpenedRef = useRef(false);
  const { error } = useToast();

  const hasChanges = useMemo(() => {
    if (!originalProfile) {
      return false;
    }

    const originalProfileData = pickFields(originalProfile.data);
    const profileData = pickFields(profile.data);

    return !isEqual(originalProfileData, profileData);
  }, [originalProfile, profile]);

  if (!originalProfile) {
    error('Can not find the original profile');
    return null;
  }

  const openEditModal = () => {
    setOpen(true);

    if (orderSpecSchema && !editModalWasOpenedRef.current) {
      setOrderSpecFormInitialInputs(orderSpecInputs);
      editModalWasOpenedRef.current = true;
    }
  };

  const closeEditModal = () => {
    setOpen(false);
  };

  const handleSave = (value: EditModalProfileData) => {
    onSave(value);
  };

  const handleResetButtonClick = () => {
    onSave({
      ...profile?.data,
      audio_tracks: originalProfile.data.audio_tracks,
      subtitle_tracks: originalProfile.data.subtitle_tracks,
      media_resolution: originalProfile.data.media_resolution
    });

    if (orderSpecSchema) {
      setWasEdited(false);
      updateOrderSpecInputs(orderSpecFormInitialInputs);
    }
  };

  useEffect(() => {
    if (!orderSpecSchema?.properties || !Object.keys(orderSpecSchema.properties).length) return;
    const orderSpecFormInitialInputsWereSet = editModalWasOpenedRef.current;
    if (orderSpecSchema && orderSpecFormInitialInputsWereSet && !isEqual(orderSpecFormInitialInputs, orderSpecInputs)) {
      setWasEdited(true);
    } else {
      setWasEdited(false);
    }
  }, [orderSpecFormInitialInputs, orderSpecInputs]);

  return (
    <Grid container item sx={{ p: 3, pt: 4 }} flexDirection="column">
      <OPProfileDetailsOutputEditModal
        key={`modal-${hasChanges}`}
        open={open}
        profile={profile}
        orderSpecSchema={orderSpecSchema}
        orderSpecInputs={orderSpecInputs}
        orderSpecSchemaFormBeforeMessage={orderSpecSchemaFormBeforeMessage}
        onClose={closeEditModal}
        onSave={handleSave}
        updateOrderSpecInputs={updateOrderSpecInputs}
      />
      <Grid container alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="h6">Output</Typography>
        {(hasChanges || wasEdited) && (
          <StyledTypographyRevisedIndicator textColor={outputRevisedLabelColor}>(Revised)</StyledTypographyRevisedIndicator>
        )}
      </Grid>
      <StyledScrollContainer>
        <OPOrderSpecSchemaFormReadOnly
          orderSpecSchema={orderSpecSchema}
          orderSpecInputs={orderSpecInputs}
          orderSpecSchemaFormBeforeMessage={orderSpecSchemaFormBeforeMessage}
        />
        {(!orderSpecSchema?.properties || !Object.keys(orderSpecSchema.properties).length) && (
          <>
            <ProfileSection label="media">
              {profile.data?.media_resolution && <ProfileChip color="primary" label="Resolution" value={profile.data?.media_resolution} />}
            </ProfileSection>

            <ProfileSection label="audio">
              {profile.data?.audio_tracks?.length &&
                profile.data?.audio_tracks?.map((track, i) => {
                  const lang = AudioLanguages?.find((l) => l.code === track);
                  return (
                    <div key={track} style={{ display: 'block', width: '100%' }}>
                      <ProfileChip
                        color="secondary"
                        label={`Track ${i + 1}`}
                        value={SimpleLabelValue({ label: lang?.label, value: track, fallback: track })}
                      />
                    </div>
                  );
                })}
            </ProfileSection>

            <ProfileSection label="subtitle">
              {profile.data?.subtitle_tracks?.length &&
                profile.data?.subtitle_tracks?.map((track, i) => {
                  const lang = SubtitleLanguages?.find((l) => l.code === track);
                  return (
                    <div key={track} style={{ display: 'block', width: '100%' }}>
                      <ProfileChip
                        color="default"
                        label={`Track ${i + 1}`}
                        value={SimpleLabelValue({ label: lang?.label, value: track, fallback: track })}
                      />
                    </div>
                  );
                })}
            </ProfileSection>
          </>
        )}
      </StyledScrollContainer>
      <Grid container item justifyContent="space-between" alignItems="center" sx={{ mt: 1 }}>
        {hasChanges || wasEdited ? <ResetButton label="Reset Profile" onClick={handleResetButtonClick} /> : <div />}
        <Button variant="outlined" size="small" color="secondary" startIcon={<PolarisIcon icon="edit" width={13} />} onClick={openEditModal}>
          Edit
        </Button>
      </Grid>
    </Grid>
  );
};

export default memo(OPProfileDetailsOutput);

const ProfileSection = ({ label, children }: { label: string; children: React.ReactNode }) => {
  return (
    <StyledGridProfileSection container className={label.toLowerCase()} alignItems="flex-start">
      <StyledGridIconLabel container item alignItems="center" xs={4} md={5} lg={4} xl={3} flexWrap="nowrap">
        <AssetIcon type={label} />
        <Box display={{ sm: 'none', md: 'block' }}>
          <label>{label}</label>
        </Box>
      </StyledGridIconLabel>
      <Grid
        container
        alignItems="center"
        item
        xs={8}
        md={7}
        lg={8}
        xl={9}
        sx={(theme) => ({ borderLeft: `1px solid ${theme.palette.action.hover}`, p: 1, minHeight: 47 })}
      >
        {children || <Typography variant="caption">-</Typography>}
      </Grid>
    </StyledGridProfileSection>
  );
};

interface ProfileChipProps {
  label: string;
  value: React.ReactNode;
  color: ChipOwnProps['color'];
  isSchema?: boolean;
}

export const ProfileChip: React.FC<ProfileChipProps> = ({ label, value, color, isSchema }) => {
  return (
    <StyledProfileChip
      size="small"
      color={color}
      label={
        <Grid container flexWrap="nowrap">
          <Grid
            item
            sx={(theme) => ({ background: 'rgba(0,0,0,.2)', p: 0.25, px: 1, ...theme.customOverrides?.ProfileChipLabel })}
            display={{ xs: isSchema ? 'block' : 'none', md: isSchema ? 'block' : 'none', lg: 'block' }}
          >
            {label}
          </Grid>
          <Grid item sx={{ p: 0.25, px: 1 }}>
            {value}
          </Grid>
        </Grid>
      }
    />
  );
};

const StyledGridIconLabel = styled(Grid)(
  ({ theme }) => `
  height: 47px;
  padding: 0;
  padding-left: ${theme.spacing(1)};
  &.MuiGrid-root {
    max-width: 110px;
  }
`
);

const StyledGridProfileSection = styled(Grid)(
  ({ theme }) => `
  padding: 0;
  background-color: ${theme.palette.background.paper};
  border-radius: ${theme.shape.borderRadius}px;
  margin-bottom: ${theme.spacing(1)};
  min-height: 47px;
  & label {
    text-transform: capitalize;
    font-weight: ${theme.typography.fontWeightBold};
    opacity: 0.6;
  }

  & .MuiBox-root {
    background: none;
    border: 0;
    padding: 0;
    margin: 0;
  }
  &.media {
    &, & svg {
      color: ${theme.palette.primary.main};
    }
  }
  &.audio {
    &, & svg {
      color: ${theme.palette.secondary.main};
    }
  }
`
);

const StyledProfileChip = styled(Chip)(
  ({ theme }) => `
  border-radius: 3px;
  overflow: hidden;
  margin: ${theme.spacing(0.5)};
  margin-right: ${theme.spacing(1)};
  line-height: 2;
  padding: 0;
  margin-left: 0;
  justify-content: flex-start;
  &.MuiChip-sizeSmall {
    padding: 0;
  }
  & .MuiChip-label {
    padding: 0;
  }
`
);

const StyledTypographyRevisedIndicator = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'textColor'
})<{
  textColor?: ThemeColors;
}>(({ theme, textColor }) => ({
  lineHeight: 1.6,
  color: textColor ? theme.palette[textColor]?.main : theme.palette.tertiary?.main,
  marginLeft: theme.spacing(1),
  fontSize: '1.1rem',
  fontStyle: 'italic'
}));

const StyledScrollContainer = styled(Box)`
  max-height: 500px;
  overflow-y: auto;
`;
